import { motion } from "framer-motion";
import { Heart, Star, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const Message = () => {
  return (
    <section className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-4"
          >
            <Sparkles className="w-12 h-12 text-yellow-400" />
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-dancing text-rose-600 mb-4">
            My Love
          </h1>
          <div className="flex justify-center items-center space-x-2">
            <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            <Heart className="w-6 h-6 text-rose-600 fill-rose-600" />
            <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
          </div>
        </motion.div>

        {/* Main Message Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-8 border border-rose-200"
        >
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <motion.p
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-xl md:text-2xl font-semibold text-rose-700 text-center mb-8"
            >
              "Every moment with you feels like a beautiful dream come true..."
            </motion.p>

            <motion.p
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              My love, from the moment our paths crossed, my life has been filled with colors I never knew existed. Your smile brightens my darkest days, and your laughter is the sweetest melody to my ears.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              You are the most beautiful person I've ever known - not just in appearance, but in the way you care, the way you love, and the way you make everything around you better. Your kindness touches everyone you meet, and your strength inspires me every day.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.1 }}
            >
              I promise to cherish every moment we share, to support your dreams as if they were my own, and to love you more with each passing day. You are my best friend, my confidant, and the love of my life.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.3 }}
              className="text-center text-2xl font-dancing text-rose-600 mt-8"
            >
              Forever Yours,
            </motion.p>

            <motion.p
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.5 }}
              className="text-center text-xl font-bold text-rose-700"
            >
            </motion.p>
          </div>
        </motion.div>

        {/* Love Quotes Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.7 }}
          className="grid md:grid-cols-3 gap-6"
        >
          {[
            {
              quote: "You are my today and all of my tomorrows.",
              author: "Leo Christopher"
            },
            {
              quote: "I saw that you were perfect, and so I loved you. Then I saw that you were not perfect and I loved you even more.",
              author: "Angelita Lim"
            },
            {
              quote: "The best thing to hold onto in life is each other.",
              author: "Audrey Hepburn"
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1.9 + index * 0.2 }}
              className="bg-gradient-to-br from-rose-100 to-pink-100 rounded-2xl p-6 text-center shadow-lg border border-rose-200"
            >
              <Heart className="w-8 h-8 text-rose-600 mx-auto mb-3" />
              <p className="text-gray-700 italic mb-3">"{item.quote}"</p>
              <p className="text-rose-600 font-semibold">- {item.author}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Floating Hearts Decoration */}
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="fixed bottom-8 right-8"
        >
          <Heart className="w-12 h-12 text-rose-400 fill-rose-400 opacity-60" />
        </motion.div>

        {/* Navigation Button */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2 }}
          className="flex justify-center mt-12"
        >
          <Link
            to="/gift"
            className="flex items-center gap-2 bg-pink-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-pink-700 transition shadow-lg text-lg"
          >
            Next
            <ArrowRight className="w-6 h-6" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Message;
